import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Loader2, RefreshCw, Search, Mail, Phone, Calendar, Users, CreditCard, CheckCircle2, Clock } from 'lucide-react';

const chip = (active) =>
  `inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
    active
      ? 'bg-[#00b4d8]/15 text-[#00b4d8] border-[#00b4d8]/40'
      : 'text-white/65 hover:text-white hover:bg-white/5 border-white/15'
  }`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

export default function BookingsTab() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/bookings/admin', { params });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((b) =>
      [b.booking_ref, b.first_name, b.last_name, b.email, b.phone, b.trip_name, b.trip_slug]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  const counts = useMemo(
    () => ({
      paid: items.filter((b) => b.paid).length,
      unpaid: items.filter((b) => !b.paid).length,
      total: items.length,
    }),
    [items]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Trip bookings</h2>
          <p className="text-sm text-white/55">Every Oman Trip request submitted from the website.</p>
        </div>
        <Button
          data-testid="admin-bookings-refresh"
          onClick={load}
          variant="outline"
          className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
        >
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="admin-bookings-filter-all" className={chip(statusFilter === 'all')} onClick={() => setStatusFilter('all')}>
          All <span className="text-white/45">{counts.total}</span>
        </button>
        <button data-testid="admin-bookings-filter-paid" className={chip(statusFilter === 'paid')} onClick={() => setStatusFilter('paid')}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Paid <span className="text-white/45">{counts.paid}</span>
        </button>
        <button data-testid="admin-bookings-filter-unpaid" className={chip(statusFilter === 'unpaid')} onClick={() => setStatusFilter('unpaid')}>
          <Clock className="h-3.5 w-3.5" /> Unpaid <span className="text-white/45">{counts.unpaid}</span>
        </button>

        <div className="relative flex-1 min-w-[220px] max-w-md ml-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
          <Input
            data-testid="admin-bookings-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, reference…"
            className="pl-9 bg-white/5 border-white/15 text-white placeholder:text-white/35"
          />
        </div>
      </div>

      <Card className="rounded-3xl border-white/10 bg-white/[0.03] backdrop-blur overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center gap-2 text-white/65">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading bookings…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-white/55">
            <div className="font-display text-lg text-white">No bookings yet</div>
            <p className="text-sm mt-1">When a customer submits the Book Trip form it will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-white/55 border-b border-white/8">
                <tr>
                  <th className="py-3 px-4">Reference</th>
                  <th className="py-3 px-4">Submitted</th>
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4">Trip</th>
                  <th className="py-3 px-4">Pax</th>
                  <th className="py-3 px-4">Payment</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr
                    key={b.id || b.booking_ref}
                    data-testid={`admin-booking-row-${b.booking_ref}`}
                    onClick={() => setSelected(b)}
                    className="border-b border-white/5 hover:bg-white/[0.04] cursor-pointer"
                  >
                    <td className="py-3 px-4 font-mono text-[12px] text-[#c8e600]">{b.booking_ref}</td>
                    <td className="py-3 px-4 text-white/75 whitespace-nowrap">{formatDate(b.created_at)}</td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-white">{b.first_name} {b.last_name}</div>
                      <div className="text-xs text-white/55">{b.email}</div>
                    </td>
                    <td className="py-3 px-4 text-white/75">{b.trip_name || b.trip_slug || '—'}</td>
                    <td className="py-3 px-4 text-white/85">{b.people_count}</td>
                    <td className="py-3 px-4">
                      {b.paid ? (
                        <Badge className="bg-[#c8e600]/15 text-[#c8e600] border border-[#c8e600]/40 hover:bg-[#c8e600]/20">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Paid
                        </Badge>
                      ) : (
                        <Badge className="bg-white/8 text-white/65 border border-white/15 hover:bg-white/10">
                          <Clock className="h-3 w-3 mr-1" /> Unpaid
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <BookingDetailDialog booking={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-white/8 last:border-b-0">
      <Icon className="h-4 w-4 text-[#00b4d8] mt-0.5 shrink-0" />
      <div className="flex-1 grid grid-cols-3 gap-3">
        <div className="text-xs uppercase tracking-wider text-white/55">{label}</div>
        <div className={`col-span-2 text-sm text-white ${mono ? 'font-mono text-[#c8e600] break-all' : ''}`}>{value || '—'}</div>
      </div>
    </div>
  );
}

function BookingDetailDialog({ booking, onClose }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="admin-booking-detail" className="max-w-xl bg-[#07101f] border-white/12 text-white p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-1.5">Booking detail</div>
          <DialogTitle className="font-display text-2xl">{booking.first_name} {booking.last_name}</DialogTitle>
          <DialogDescription className="text-white/55 text-xs">Reference {booking.booking_ref}</DialogDescription>
        </DialogHeader>
        <div className="p-6">
          <Row icon={Calendar} label="Submitted" value={formatDate(booking.created_at)} />
          <Row icon={Users} label="Trip" value={booking.trip_name || booking.trip_slug} />
          <Row icon={Users} label="Headcount" value={`${booking.people_count} pax`} />
          <Row icon={Calendar} label="Date of birth" value={booking.date_of_birth} />
          <Row icon={Mail} label="Email" value={booking.email} />
          <Row icon={Phone} label="Phone" value={booking.phone} />

          {booking.paid ? (
            <div className="mt-4 rounded-2xl border border-[#c8e600]/30 bg-[#c8e600]/[0.06] p-4 space-y-1">
              <div className="flex items-center gap-2 text-[#c8e600] font-semibold"><CheckCircle2 className="h-4 w-4" /> Paid via PayPal</div>
              <Row icon={CreditCard} label="PayPal txn" value={booking.paypal_transaction_id} mono />
              <Row icon={CreditCard} label="Amount captured" value={`${booking.paypal_captured_amount || ''} ${booking.paypal_captured_currency || ''}`} />
              <Row icon={Calendar} label="Paid at" value={formatDate(booking.paid_at)} />
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-2 text-white/65 text-sm">
              <Clock className="h-4 w-4" /> Awaiting payment
            </div>
          )}

          <div className="mt-5 flex gap-2">
            <a href={`mailto:${booking.email}`} className="flex-1">
              <Button className="w-full bg-[#0078d4] hover:bg-[#0066b8] text-white rounded-xl"><Mail className="mr-2 h-4 w-4" /> Email customer</Button>
            </a>
            <a href={`tel:${(booking.phone || '').replace(/\s/g, '')}`} className="flex-1">
              <Button variant="outline" className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl">
                <Phone className="mr-2 h-4 w-4" /> Call
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
